import { useState, useCallback } from 'react';
import ImageUpload from './ImageUpload';

const ProfileImageUpload = ({
  onImageSelect,
  onImageRemove,
  currentImage = null,
  maxSize = 2 * 1024 * 1024, // 2MB default for profile images
  disabled = false,
  className = '',
  loading = false,
  error = null,
  required = false,
  label = 'Profile Picture',
  shape = 'circle', // 'circle' or 'square'
  size = 'large' // 'small', 'medium', 'large'
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentImage);

  const sizeClasses = {
    small: 'w-16 h-16',
    medium: 'w-24 h-24',
    large: 'w-32 h-32'
  };

  const handleImageSelect = useCallback((files) => {
    if (files.length > 0) {
      const file = files[0];
      const preview = URL.createObjectURL(file);
      
      setSelectedImage(file);
      setPreviewUrl(preview);
      onImageSelect(file);
    }
  }, [onImageSelect]);

  const handleImageRemove = useCallback(() => {
    if (previewUrl && previewUrl !== currentImage) {
      URL.revokeObjectURL(previewUrl);
    }
    
    setSelectedImage(null);
    setPreviewUrl(null);
    onImageRemove();
  }, [previewUrl, currentImage, onImageRemove]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`profile-image-upload ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-jet-gray-700 mb-3">
          {label} {required && <span className="text-jet-red-500">*</span>}
        </label>
      )}

      <div className="flex items-start space-x-6">
        {/* Current/Preview Image */}
        <div className="flex-shrink-0">
          <div className={`
            ${sizeClasses[size]} 
            ${shape === 'circle' ? 'rounded-full' : 'rounded-lg'} 
            overflow-hidden bg-jet-gray-100 border-2 border-jet-gray-200 relative
          `}>
            {previewUrl ? (
              <>
                <img
                  src={previewUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
                {loading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <svg className="w-8 h-8 text-jet-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Upload Controls */}
        <div className="flex-1">
          <ImageUpload
            onImageSelect={handleImageSelect}
            onImageRemove={handleImageRemove}
            maxSize={maxSize}
            maxFiles={1}
            multiple={false}
            disabled={disabled}
            loading={loading}
            error={error}
            required={required}
            label=""
            showPreview={false}
            className="mb-4"
          >
            {/* Custom upload area for profile image */}
            <div className="flex flex-col items-center py-4">
              <svg className="w-8 h-8 text-jet-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              <p className="text-sm text-jet-gray-600 mb-1">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-jet-gray-500">
                PNG, JPG up to {formatFileSize(maxSize)}
              </p>
            </div>
          </ImageUpload>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            {previewUrl && (
              <button
                type="button"
                onClick={handleImageRemove}
                disabled={loading}
                className="text-sm text-jet-red-600 hover:text-jet-red-700 font-medium disabled:opacity-50"
              >
                Remove Photo
              </button>
            )}
            
            {previewUrl && previewUrl !== currentImage && (
              <button
                type="button"
                onClick={() => window.open(previewUrl, '_blank')}
                className="text-sm text-jet-blue-600 hover:text-jet-blue-700 font-medium"
              >
                View Full Size
              </button>
            )}
          </div>

          {/* File Info */}
          {selectedImage && (
            <div className="mt-3 p-3 bg-jet-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-jet-gray-900">
                    {selectedImage.name}
                  </p>
                  <p className="text-xs text-jet-gray-500">
                    {formatFileSize(selectedImage.size)} • {selectedImage.type}
                  </p>
                </div>
                
                {loading && (
                  <div className="flex items-center text-xs text-jet-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-jet-blue-500 mr-2"></div>
                    Uploading...
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Guidelines */}
          <div className="mt-4 text-xs text-jet-gray-500">
            <p className="mb-1">Guidelines:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Use a high-quality image for best results</li>
              <li>Square images work best for profile pictures</li>
              <li>Avoid images with text or logos</li>
              <li>Make sure your face is clearly visible</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileImageUpload;